import React, { FC, useMemo, useRef, useState } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import {
  AdvanceDealDetails,
  AdvanceDealDrmIssue,
  AdvanceDealDrmPlatform,
  AdvanceDealPlatformRevenue
} from "../../../../../types"
import { STATUS_STYLE } from "./dealStatus"
import Loading from "../../../../ui/Loading"

const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const FONT = "Inter, system-ui, sans-serif"

type PlatformKey = "spotify" | "youtube" | "youtubeAudioTier" | "appleMusic"
type SeriesKey = "all" | PlatformKey | "other"

type SeriesDef = { key: SeriesKey; label: string; color: string }

const SERIES: SeriesDef[] = [
  { key: "all", label: "All", color: "#232637" },
  { key: "spotify", label: "Spotify", color: "#168F59" },
  { key: "youtube", label: "YouTube", color: "#DC143C" },
  { key: "youtubeAudioTier", label: "YouTube Audio", color: "#F26B2A" },
  { key: "appleMusic", label: "Apple Music", color: "#BA57A0" },
  { key: "other", label: "Other", color: "#8B96A8" }
]

// A platform that sent no report in any month is one the artist does not use, not one that is missing.
const MAIN_PLATFORMS: PlatformKey[] = ["spotify", "youtube", "youtubeAudioTier", "appleMusic"]

const EVENTS_LEGEND = "EVENTS · A# Advance · DM Discovery Mode · D DRM detected · FX DRM action · CR Company recoup"

const RECOUP_FILL = "rgba(148, 163, 184, 0.14)"
const RECOUP_BORDER = "#cbd5e1"

const W = 1200
const EVENT_BAND_H = 54
const H = 380 + EVENT_BAND_H
const PAD_L = 60
const PAD_R = 56
const PAD_T = 16
const X_AXIS_H = 30
const PLOT_BOTTOM_MAX = H - X_AXIS_H - EVENT_BAND_H
const ADVANCE_COLOR = "#536a99"
const DRM_COLOR = "#cc4d54"
const FIX_COLOR = "#c99235"
const DM_COLOR = "#5777c9"
const CR_COLOR = "#468e92"
const DM_MAX_TRACKS = 3
// Only issues that touch more than this share of the artist's revenue get a marker.
const DRM_MIN_SHARE = 5
const DRM_PLATFORMS = [
  { key: "spotify", label: "Spotify" },
  { key: "appleMusic", label: "Apple Music" },
  { key: "youtube", label: "YouTube" }
] as const
const EVENT_R = 11

// Only a platform's share above the minimum is worth a marker
const worthShowing = (found: AdvanceDealDrmPlatform | null) =>
  !!found && found.revenueShare !== null && found.revenueShare > DRM_MIN_SHARE

type ReportingStatus = "complete" | "partial" | "missing"

const STATUS_LABEL: Record<ReportingStatus, string> = { complete: "Complete", partial: "Partial", missing: "Missing" }

const usd2 = (value: number) =>
  `${value < 0 ? "-" : ""}$${Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`

const usd0 = (value: number) => `${value < 0 ? "-" : ""}$${Math.round(Math.abs(value)).toLocaleString("en-US")}`

const axisUsd = (value: number) => {
  if (value === 0) return "$0"
  const abs = Math.abs(value)
  const text = abs >= 1000 ? `${Number((abs / 1000).toFixed(1))}K` : `${Math.round(abs)}`
  return `${value < 0 ? "-" : ""}$${text}`
}

// Backend months are "YYYYMM".
const monthLabel = (key: string) => {
  const month = parseInt(key.slice(4, 6), 10)
  if (key.length < 6 || isNaN(month)) return key
  return new Date(parseInt(key.slice(0, 4), 10), month - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric"
  })
}

// Round the top of the axis up to a tidy step so the ticks read as $0, $500, $1K.
const niceStep = (range: number) => {
  const rough = range / 4
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
  const fraction = rough / magnitude
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10
  return nice * magnitude
}

type Pt = [number, number]

const hexPoints = (cx: number, cy: number, r: number) =>
  [0, 1, 2, 3, 4, 5]
    .map((k) => {
      const angle = (Math.PI / 180) * (60 * k - 30)
      return `${(cx + r * Math.cos(angle)).toFixed(1)},${(cy + r * Math.sin(angle)).toFixed(1)}`
    })
    .join(" ")

// Smooth curve through the points that never overshoots them (monotone cubic), as one cubic
// segment per pair of neighbours so a segment can be styled on its own.
const curveSegments = (pts: Pt[]): string[] => {
  const count = pts.length
  if (count < 2) return []
  const slopes = pts.slice(1).map((p, k) => (p[1] - pts[k][1]) / (p[0] - pts[k][0]))
  const tangents = pts.map((_, k) =>
    k === 0
      ? slopes[0]
      : k === count - 1
      ? slopes[count - 2]
      : slopes[k - 1] * slopes[k] <= 0
      ? 0
      : (slopes[k - 1] + slopes[k]) / 2
  )
  slopes.forEach((slope, k) => {
    if (slope === 0) {
      tangents[k] = 0
      tangents[k + 1] = 0
      return
    }
    const a = tangents[k] / slope
    const b = tangents[k + 1] / slope
    const sum = a * a + b * b
    if (sum > 9) {
      const tau = 3 / Math.sqrt(sum)
      tangents[k] = tau * a * slope
      tangents[k + 1] = tau * b * slope
    }
  })
  return pts.slice(1).map((p, k) => {
    const [x0, y0] = pts[k]
    const dx = (p[0] - x0) / 3
    return `M${x0},${y0} C${x0 + dx},${y0 + tangents[k] * dx} ${p[0] - dx},${p[1] - tangents[k + 1] * dx} ${p[0]},${
      p[1]
    }`
  })
}

type MonthPoint = {
  row: AdvanceDealPlatformRevenue
  status: ReportingStatus
  missing: string[]
  recoupPct: number | null
  recouped: number
  deployed: number
}

type Tip = {
  x: number
  y: number
  accent: string
  // Event tooltips use a dark body, like the example
  dark?: boolean
  title: string
  sub: string
  subExtra?: string
  rows: { label: string; value: string }[]
  // Further blocks under the rows, each with its own heading and optional footnote
  sections?: {
    heading?: string
    rows: { label: string; value: string }[]
    note?: string
    // What was done about it, shown in its own highlighted block
    action?: string
    // Plain lines, e.g. track titles
    list?: string[]
  }[]
  missing?: string[]
}

type PlatformRevenueChartProps = {
  details: AdvanceDealDetails | null
  loading: boolean
  failed: boolean
  artistName: string
  reportingPeriod: boolean
  // While the revenue is limited to DRM-affected tracks: "all", "dm-all", "issue:YYYYMM", "fix:YYYYMM" or "dm:YYYYMM"
  drmFilter: string | null
  onDrmFilterChange: (next: string | null) => void
  // The filtered revenue is still on its way; the previous figures stay up, dimmed
  filtering: boolean
}

const PlatformRevenueChart: FC<PlatformRevenueChartProps> = ({
  details,
  loading,
  failed,
  artistName,
  reportingPeriod,
  drmFilter,
  onDrmFilterChange,
  filtering
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [tip, setTip] = useState<Tip | null>(null)
  const [wrapWidth, setWrapWidth] = useState(W)
  // Legend entries switched off by clicking them: series keys, plus "recoup" for the grey area
  const [hidden, setHidden] = useState<string[]>([])

  const model = useMemo(() => {
    const reported = details?.platformRevenue ?? []
    if (reported.length === 0) return null

    // One slot per month from the first advance to the current month. A month with no row at all
    // has had no report from any platform.
    const monthIndex = (key: string) => parseInt(key.slice(0, 4), 10) * 12 + parseInt(key.slice(4, 6), 10) - 1
    const monthKey = (index: number) => `${Math.floor(index / 12)}${String((index % 12) + 1).padStart(2, "0")}`
    const deployments = (details?.advances ?? []).map((a) => a.deploymentMonth).sort()
    const startIndex = monthIndex(deployments[0] ?? reported[0].month)
    // Activity-period months run 2 behind the reporting period the as-of date belongs to.
    const endIndex =
      monthIndex(details?.asOf.replace(/-/g, "").slice(0, 6) ?? reported[reported.length - 1].month) -
      (reportingPeriod ? 0 : 2)
    const byMonth = new Map(reported.map((row) => [row.month, row]))
    const rows: AdvanceDealPlatformRevenue[] = []
    for (let index = startIndex; index <= Math.max(endIndex, startIndex); index++) {
      const key = monthKey(index)
      rows.push(
        byMonth.get(key) ?? {
          month: key,
          spotify: null,
          youtube: null,
          youtubeAudioTier: null,
          appleMusic: null,
          other: null,
          total: 0,
          otherPlatformCount: 0,
          otherRevenuePlatformCount: 0
        }
      )
    }

    const mainKeys = MAIN_PLATFORMS.filter((key) => rows.some((row) => row[key] !== null && row[key] !== undefined))
    const labelOf = (key: SeriesKey) => SERIES.find((s) => s.key === key)?.label ?? key

    const cumulativeByMonth = new Map((details?.cumulatives ?? []).map((c) => [c.month, c]))

    const points: MonthPoint[] = rows.map((row) => {
      const missing = mainKeys.filter((key) => row[key] === null || row[key] === undefined)
      const status: ReportingStatus =
        missing.length === 0 ? "complete" : missing.length === mainKeys.length ? "missing" : "partial"
      const cumulative = cumulativeByMonth.get(row.month)
      const recoupPct =
        cumulative && cumulative.deployed > 0 ? Math.min(100, (cumulative.recouped / cumulative.deployed) * 100) : null
      return {
        row,
        status,
        missing: missing.map(labelOf),
        recoupPct,
        recouped: cumulative?.recouped ?? 0,
        deployed: cumulative?.deployed ?? 0
      }
    })

    // The axis fits whatever is switched on
    const values = rows.flatMap((row) =>
      SERIES.filter((s) => !hidden.includes(s.key)).flatMap((s) => {
        const value = s.key === "all" ? row.total : row[s.key]
        return typeof value === "number" ? [value] : []
      })
    )
    const top = Math.max(0, ...values)
    const bottom = Math.min(0, ...values)
    const step = niceStep(Math.max(top - bottom, 1))
    const yMax = Math.ceil(top / step) * step || step
    const yMin = Math.floor(bottom / step) * step
    const ticks: number[] = []
    for (let tick = yMin; tick <= yMax + step / 2; tick += step) ticks.push(tick)

    const visibleSeries = SERIES.filter(
      (s) => s.key === "all" || s.key === "other" || mainKeys.includes(s.key as PlatformKey)
    )

    return { points, mainKeys, yMin, yMax, ticks, visibleSeries }
  }, [details, reportingPeriod, hidden])

  // The filter is for the DRM issue and fix markers on the chart, so it needs at least one in view
  const hasVisibleIssues =
    !!model &&
    [...(details?.drmActions?.issues ?? []), ...(details?.drmActions?.fixes ?? [])].some(
      (entry) =>
        model.points.some((point) => point.row.month === entry.month) &&
        DRM_PLATFORMS.some(({ key }) => worthShowing(entry[key]))
    )
  const filterDisabled = filtering || (!hasVisibleIssues && !drmFilter)
  const hasVisibleDiscovery =
    !!model &&
    (details?.drmActions?.discoveryMode ?? []).some(
      (entry) => entry.tracks.length > 0 && model.points.some((point) => point.row.month === entry.month)
    )
  const discoveryDisabled = filtering || (!hasVisibleDiscovery && !drmFilter)

  const filterButton = (props: {
    label: string
    value: string
    color: string
    disabled: boolean
    emptyHint: string
    hasMarkers: boolean
  }) => {
    const active = drmFilter === props.value
    return (
      <button
        type="button"
        onClick={() => onDrmFilterChange(active ? null : props.value)}
        disabled={props.disabled}
        title={!props.hasMarkers && !drmFilter ? props.emptyHint : undefined}
        style={{
          padding: "5px 12px",
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: FONT,
          cursor: props.disabled ? "not-allowed" : "pointer",
          opacity: props.disabled && !filtering ? 0.5 : 1,
          border: `1px solid ${active ? props.color : "#d1d5db"}`,
          background: active ? props.color : "#fff",
          color: active ? "#fff" : TEXT_MUTED
        }}
      >
        {props.label}
      </button>
    )
  }

  // Clicking a legend entry shows or hides it; one plot always stays on
  const toggleSeries = (key: string) => {
    setTip(null)
    setHidden((current) => {
      if (current.includes(key)) return current.filter((k) => k !== key)
      const plots = model?.visibleSeries.filter((s) => !current.includes(s.key)) ?? []
      return key !== "recoup" && plots.length <= 1 ? current : [...current, key]
    })
  }
  const legendItemStyle = (key: string): React.CSSProperties => ({
    cursor: "pointer",
    userSelect: "none",
    opacity: hidden.includes(key) ? 0.4 : 1
  })

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[120px]">
          <Loading />
        </div>
      )
    }
    if (failed || !details) {
      return <KSpan text="The platform revenue could not be loaded." fontSize={13} color={TEXT_MUTED} />
    }
    if (!model) {
      return <KSpan text="No platform revenue has been reported yet." fontSize={13} color={TEXT_MUTED} />
    }

    const { points, yMin, yMax, ticks, visibleSeries } = model
    const n = points.length
    const plotRight = W - PAD_R
    const plotTop = PAD_T
    const plotH = PLOT_BOTTOM_MAX - plotTop
    // Each month owns one slot and is plotted at its centre, so the recoupment area can start from
    // 0% at the left edge, the moment of the first deployment.
    const slot = (plotRight - PAD_L) / n
    const x = (i: number) => PAD_L + (i + 0.5) * slot
    const y = (v: number) => plotTop + plotH - ((v - yMin) / (yMax - yMin)) * plotH
    const yPct = (v: number) => plotTop + plotH - (v / 100) * plotH

    // Month only, with the two-digit year on the first month and wherever the year changes.
    const yearOf = (i: number) => points[i].row.month.slice(0, 4)
    const yearStarts = (i: number) => i === 0 || yearOf(i) !== yearOf(i - 1)
    const axisLabel = (i: number) => {
      const month = monthLabel(points[i].row.month).slice(0, 3)
      return yearStarts(i) ? `${month} '${yearOf(i).slice(2)}` : month
    }

    const showTip = (next: Omit<Tip, "x" | "y">, px: number, py: number) => {
      setWrapWidth(wrapRef.current?.getBoundingClientRect().width ?? W)
      setTip({ ...next, x: px, y: py })
    }

    const valueOf = (point: MonthPoint, key: SeriesKey): number | null => {
      const value = key === "all" ? point.row.total : point.row[key]
      return value === null || value === undefined ? null : value
    }

    const platformTip = (series: SeriesDef, point: MonthPoint, value: number) => {
      const total = point.row.total
      const share = total !== 0 ? `${((value / total) * 100).toFixed(1)}%` : "N/A"
      return {
        accent: series.color,
        title: series.label,
        sub: monthLabel(point.row.month),
        subExtra:
          series.key === "other"
            ? `${point.row.otherRevenuePlatformCount} / ${point.row.otherPlatformCount} platforms`
            : undefined,
        rows: [
          { label: "Revenue collected", value: usd2(value) },
          { label: "Share of month", value: share }
        ]
      }
    }

    const allTip = (point: MonthPoint) => ({
      accent: SERIES[0].color,
      title: "All",
      sub: monthLabel(point.row.month),
      rows: [
        { label: "Gross revenue", value: usd2(point.row.total) },
        { label: "Reporting status", value: STATUS_LABEL[point.status] }
      ],
      missing: point.missing.length > 0 ? point.missing : undefined
    })

    const recoupTip = (point: MonthPoint) => ({
      accent: RECOUP_BORDER,
      title: "Company recoupment %",
      sub: monthLabel(point.row.month),
      rows: [
        { label: "Recouped", value: `${(point.recoupPct ?? 0).toFixed(1)}%` },
        { label: "Principal", value: usd2(point.recouped) },
        { label: "Deployed", value: usd0(point.deployed) }
      ]
    })

    // Straight runs of consecutive months that have a value; a gap in the reports breaks the line.
    const runsFor = (key: SeriesKey) => {
      const runs: Pt[][] = []
      let current: Pt[] = []
      points.forEach((point, i) => {
        const value = valueOf(point, key)
        if (value === null) {
          if (current.length > 1) runs.push(current)
          current = []
          return
        }
        current.push([x(i), y(value)])
      })
      if (current.length > 1) runs.push(current)
      return runs.map((run) => curveSegments(run).join(" "))
    }

    // Company recoupment area over the months that have a running total.
    const recoupRuns: number[][] = []
    let run: number[] = []
    points.forEach((point, i) => {
      if (point.recoupPct === null) {
        if (run.length > 0) recoupRuns.push(run)
        run = []
        return
      }
      run.push(i)
    })
    if (run.length > 0) recoupRuns.push(run)

    const incomplete = points.filter((point) => point.status !== "complete")
    // One sentence: the latest incomplete month, plus a count of the earlier ones.
    const latest = incomplete[incomplete.length - 1]
    const olderCount = incomplete.length - 1
    const missingPlatforms = Array.from(new Set(incomplete.flatMap((point) => point.missing)))
    const noteMonths = latest
      ? `${monthLabel(latest.row.month)}${
          olderCount > 0 ? ` and ${olderCount} earlier ${olderCount === 1 ? "month" : "months"}` : ""
        }`
      : ""
    const noteText = !latest
      ? ""
      : incomplete.every((point) => point.status === "missing")
      ? `${noteMonths} ${olderCount > 0 ? "have" : "has"} no platform reports yet.`
      : `${noteMonths} ${olderCount > 0 ? "are" : "is"} partial; ${missingPlatforms.join(
          ", "
        )} revenue has not yet been reported.`

    // One marker per advance, on its deployment month. Several in one month sit side by side.
    const monthSlot = new Map(points.map((point, i) => [point.row.month, i]))
    // Markers on the same month sit side by side, advances first.
    const drmIssues = (details?.drmActions?.issues ?? []).filter(
      (issue) => monthSlot.has(issue.month) && DRM_PLATFORMS.some(({ key }) => worthShowing(issue[key]))
    )
    const markersPerMonth = new Map<string, number>()
    const countMarker = (month: string) => markersPerMonth.set(month, (markersPerMonth.get(month) ?? 0) + 1)
    ;(details?.advances ?? []).forEach(
      (advance) => monthSlot.has(advance.deploymentMonth) && countMarker(advance.deploymentMonth)
    )
    drmIssues.forEach((issue) => countMarker(issue.month))
    const drmFixes = (details?.drmActions?.fixes ?? []).filter(
      (fix) => monthSlot.has(fix.month) && DRM_PLATFORMS.some(({ key }) => worthShowing(fix[key]))
    )
    drmFixes.forEach((fix) => countMarker(fix.month))
    const discoveryMode = (details?.drmActions?.discoveryMode ?? []).filter(
      (entry) => monthSlot.has(entry.month) && entry.tracks.length > 0
    )
    discoveryMode.forEach((entry) => countMarker(entry.month))
    // Company recoup happens once, and only when the deal has actually recouped.
    const recoupMonth =
      details?.artist.recouped && details.artist.companyRecoupDate && monthSlot.has(details.artist.companyRecoupDate)
        ? details.artist.companyRecoupDate
        : null
    if (recoupMonth) countMarker(recoupMonth)
    const placed = new Map<string, number>()
    const markerX = (month: string) => {
      const position = placed.get(month) ?? 0
      placed.set(month, position + 1)
      const offset = (position - ((markersPerMonth.get(month) ?? 1) - 1) / 2) * (EVENT_R * 2 + 6)
      return x(monthSlot.get(month) ?? 0) + offset
    }
    let cumulativeDeployed = 0
    const advanceEvents = (details?.advances ?? []).flatMap((advance) => {
      cumulativeDeployed += advance.amount
      if (!monthSlot.has(advance.deploymentMonth)) return []
      return [{ advance, cumulative: cumulativeDeployed, px: markerX(advance.deploymentMonth) }]
    })
    const totalTracks = details?.artist.trackCount
    // Issues and fixes share the same affected-scope block; a fix adds what was done about it.
    const scopeSections = (entry: AdvanceDealDrmIssue, show: (found: AdvanceDealDrmPlatform) => boolean) =>
      DRM_PLATFORMS.flatMap(({ key, label }) => {
        const found = entry[key]
        if (!found || !show(found)) return []
        const songShare =
          totalTracks && totalTracks > 0 ? ` (${((found.tracksAffected / totalTracks) * 100).toFixed(1)}%)` : ""
        return [
          {
            rows: [
              { label: "Platform", value: label },
              {
                label: "Songs",
                value: `${found.tracksAffected}${totalTracks ? ` / ${totalTracks}` : ""}${songShare}`
              },
              { label: "Affected song revenue", value: `${usd2(found.avgMonthlyRevenue)} / month` },
              {
                label: "Share of total revenue",
                value: found.revenueShare === null ? "N/A" : `${found.revenueShare.toFixed(1)}%`
              }
            ],
            note: `Basis: average monthly revenue of the ${found.tracksAffected} affected ${
              found.tracksAffected === 1 ? "song" : "songs"
            } on ${label} over the 3 months before.`,
            action: found.actionLog
          }
        ]
      }).map((section, index) => ({ ...section, heading: index === 0 ? "Affected Scope" : undefined }))
    const discoveryEvents = discoveryMode.map((entry) => ({
      month: entry.month,
      px: markerX(entry.month),
      tracks: entry.tracks
    }))
    // Recovery timing totals, as in the Advance Recoupment Schedule's total row
    const scheduleAdvances = details?.advances ?? []
    const actualMonths = scheduleAdvances.map((a) => (a.isActual ? a.actualRecoupMonths : a.projectedRecoupMonths))
    const targetTotal = scheduleAdvances.reduce((total, a) => total + a.targetMonths, 0)
    const actualTotal = actualMonths.every((m) => m !== null && m !== undefined)
      ? (actualMonths as number[]).reduce((total, m) => total + m, 0)
      : null
    const recoupDelta = actualTotal === null ? null : targetTotal - actualTotal
    const recoupStatus =
      recoupDelta === null ? null : recoupDelta > 2 ? "ahead" : recoupDelta < -2 ? "timing_review" : "on_track"
    const recoupEvent = recoupMonth ? { month: recoupMonth, px: markerX(recoupMonth) } : null
    const drmEvents = [
      ...drmIssues.map((issue) => ({
        kind: "issue" as const,
        month: issue.month,
        px: markerX(issue.month),
        sections: scopeSections(issue, worthShowing)
      })),
      ...drmFixes.map((fix) => ({
        kind: "fix" as const,
        month: fix.month,
        px: markerX(fix.month),
        sections: scopeSections(fix, worthShowing)
      }))
    ]
    const eventY = PLOT_BOTTOM_MAX + EVENT_BAND_H / 2 + 4

    const first = monthLabel(points[0].row.month)
    const last = monthLabel(points[n - 1].row.month)
    const trackCount = details?.artist.trackCount
    const subtitle = [
      artistName,
      trackCount === undefined || trackCount === null
        ? null
        : `${trackCount.toLocaleString("en-US")} ${trackCount === 1 ? "track" : "tracks"}`,
      first === last ? first : `${first}–${last}`,
      "USD"
    ]
      .filter(Boolean)
      .join(" · ")
    const scale = wrapWidth / W
    const tipLeft = tip ? tip.x * scale : 0
    const flip = tip ? tipLeft > wrapWidth - 280 : false

    return (
      <>
        <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
          <KSpan text={subtitle} fontSize={13} color={TEXT_MUTED} />
          {drmFilter && (
            <KSpan
              text={
                drmFilter === "all"
                  ? "Showing revenue of the tracks affected by DRM issues"
                  : drmFilter === "dm-all"
                  ? "Showing revenue of the tracks put into Discovery Mode"
                  : drmFilter.startsWith("dm:")
                  ? `Showing revenue of the tracks put into Discovery Mode in ${monthLabel(drmFilter.split(":")[1])}`
                  : `Showing revenue of the tracks affected by the ${monthLabel(drmFilter.split(":")[1])} DRM ${
                      drmFilter.startsWith("fix:") ? "fix" : "issue"
                    }`
              }
              fontSize={13}
              color={DRM_COLOR}
            />
          )}
        </div>

        <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2">
          {visibleSeries.map((series) => (
            <div
              key={series.key}
              className="flex items-center gap-1.5"
              style={legendItemStyle(series.key)}
              onClick={() => toggleSeries(series.key)}
            >
              <span style={{ width: 22, borderTop: `3px solid ${series.color}`, display: "inline-block" }} />
              <KSpan
                text={series.label.toUpperCase()}
                fontSize={12.5}
                fontWeight={600}
                color={TEXT_MUTED}
                letterSpacing="0.03em"
                lineHeight="18px"
              />
            </div>
          ))}
          <div
            className="flex items-center gap-1.5"
            style={legendItemStyle("recoup")}
            onClick={() => toggleSeries("recoup")}
          >
            <span
              style={{
                width: 20,
                height: 14,
                borderRadius: 2,
                border: `1px solid ${RECOUP_BORDER}`,
                background: RECOUP_FILL,
                display: "inline-block"
              }}
            />
            <KSpan
              text="COMPANY RECOUPMENT %"
              fontSize={12.5}
              fontWeight={600}
              color={TEXT_MUTED}
              letterSpacing="0.03em"
              lineHeight="18px"
            />
          </div>
        </div>

        <div ref={wrapRef} className="relative" style={{ fontFamily: FONT }}>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              overflow: "visible",
              opacity: filtering ? 0.3 : 1,
              transition: "opacity 0.15s"
            }}
            onMouseLeave={() => setTip(null)}
          >
            {/* Left axis: gross revenue */}
            {ticks.map((tick) => {
              const yy = Math.round(y(tick)) + 0.5
              return (
                <g key={tick}>
                  <line x1={PAD_L} x2={plotRight} y1={yy} y2={yy} stroke="#f1f5f9" />
                  <text x={PAD_L - 8} y={yy + 3} textAnchor="end" fontSize={10.5} fill="#666">
                    {axisUsd(tick)}
                  </text>
                </g>
              )
            })}
            <text
              x={14}
              y={plotTop + plotH / 2}
              textAnchor="middle"
              fontSize={10.5}
              fill="#666"
              transform={`rotate(-90 14 ${plotTop + plotH / 2})`}
            >
              Gross Revenue (USD)
            </text>

            {/* Right axis: company recoupment %, muted so it never competes with the revenue axis */}
            {[0, 25, 50, 75, 100].map((tick) => (
              <text key={tick} x={plotRight + 8} y={yPct(tick) + 3} fontSize={10.5} fill="#b6bfcc">
                {tick}%
              </text>
            ))}
            <text
              x={W - 6}
              y={plotTop + plotH / 2}
              textAnchor="middle"
              fontSize={10.5}
              fill="#b6bfcc"
              transform={`rotate(-90 ${W - 6} ${plotTop + plotH / 2})`}
            >
              Recoupment %
            </text>

            {/* Company recoupment area sits at the back */}
            {(hidden.includes("recoup") ? [] : recoupRuns).map((indices) => {
              const startsAtDeal = indices[0] === 0
              const pts: Pt[] = indices.map((i) => [x(i), yPct(points[i].recoupPct ?? 0)])
              if (startsAtDeal) pts.unshift([PAD_L, yPct(0)])
              const curve = curveSegments(pts)
                .map((segment, k) => (k === 0 ? segment : segment.replace(/^M[^C]*/, "")))
                .join(" ")
              const end = pts[pts.length - 1][0]
              return (
                <path
                  key={indices[0]}
                  d={pts.length < 2 ? "" : `${curve} L${end},${yPct(0)} L${pts[0][0]},${yPct(0)} Z`}
                  fill={RECOUP_FILL}
                  stroke="none"
                />
              )
            })}
            {points.map((point, i) =>
              point.recoupPct === null || hidden.includes("recoup") ? null : (
                <rect
                  key={point.row.month}
                  x={x(i) - slot / 2}
                  y={plotTop}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => showTip(recoupTip(point), x(i), yPct(point.recoupPct ?? 0))}
                />
              )
            )}

            {/* Base line and month labels */}
            <line
              x1={PAD_L}
              x2={plotRight}
              y1={Math.round(y(0)) + 0.5}
              y2={Math.round(y(0)) + 0.5}
              stroke="rgba(0,0,0,0.1)"
            />
            {points.map((point, i) => (
              <text key={point.row.month} x={x(i)} y={H - 8} textAnchor="middle" fontSize={10.5} fill="#666">
                {axisLabel(i)}
              </text>
            ))}

            {/* Event row, just above the month labels */}
            <line x1={PAD_L} x2={plotRight} y1={eventY} y2={eventY} stroke="#f1f5f9" />
            {advanceEvents.map(({ advance, cumulative, px }) => (
              <g
                key={advance.advanceId}
                onMouseEnter={() =>
                  showTip(
                    {
                      accent: ADVANCE_COLOR,
                      dark: true,
                      title: `ADVANCE #${advance.advanceNumber}`,
                      sub: monthLabel(advance.deploymentMonth),
                      rows: [
                        { label: "Advance Amount", value: usd0(advance.amount) },
                        { label: "Cumulative Deployed", value: usd0(cumulative) }
                      ]
                    },
                    px,
                    eventY
                  )
                }
              >
                <polygon
                  points={hexPoints(px, eventY, EVENT_R)}
                  fill="#fff"
                  stroke={ADVANCE_COLOR}
                  strokeWidth={1.75}
                />
                <text
                  x={px}
                  y={eventY + 3.5}
                  textAnchor="middle"
                  fontSize={9.5}
                  fontWeight={700}
                  fill={ADVANCE_COLOR}
                  pointerEvents="none"
                >
                  {`A${advance.advanceNumber}`}
                </text>
              </g>
            ))}
            {discoveryEvents.map(({ month, px, tracks }) => {
              const selected = drmFilter === `dm:${month}`
              return (
                <g
                  key={`dm-${month}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => onDrmFilterChange(selected ? null : `dm:${month}`)}
                  onMouseEnter={() =>
                    showTip(
                      {
                        accent: DM_COLOR,
                        dark: true,
                        title: "DISCOVERY MODE",
                        sub: monthLabel(month),
                        rows: [
                          { label: "Platform", value: "Spotify" },
                          { label: "Action", value: "Discovery Mode activated" }
                        ],
                        sections: [
                          {
                            heading: `Tracks (${tracks.length})`,
                            rows: [],
                            list: [
                              ...tracks.slice(0, DM_MAX_TRACKS),
                              ...(tracks.length > DM_MAX_TRACKS ? [`+ ${tracks.length - DM_MAX_TRACKS} more`] : [])
                            ]
                          }
                        ]
                      },
                      px,
                      eventY
                    )
                  }
                >
                  <circle
                    cx={px}
                    cy={eventY}
                    r={EVENT_R}
                    fill={selected ? DM_COLOR : "#fff"}
                    stroke={DM_COLOR}
                    strokeWidth={selected ? 2.5 : 1.75}
                  />
                  <text
                    x={px}
                    y={eventY + 3.5}
                    textAnchor="middle"
                    fontSize={8.5}
                    fontWeight={700}
                    fill={selected ? "#fff" : DM_COLOR}
                    pointerEvents="none"
                  >
                    DM
                  </text>
                </g>
              )
            })}
            {recoupEvent && (
              <g
                onMouseEnter={() =>
                  showTip(
                    {
                      accent: CR_COLOR,
                      dark: true,
                      title: "COMPANY RECOUPMENT",
                      sub: monthLabel(recoupEvent.month),
                      rows: [],
                      sections: [
                        {
                          heading: "Recovery Timing",
                          rows: [
                            { label: "Actual", value: actualTotal === null ? "N/A" : `${Math.round(actualTotal)} mo` },
                            { label: "Target", value: `${Math.round(targetTotal)} mo` },
                            {
                              label: "Delta",
                              value:
                                recoupDelta === null
                                  ? "N/A"
                                  : `${recoupDelta > 0 ? "+" : ""}${Math.round(recoupDelta)} mo`
                            },
                            {
                              label: "Status",
                              value: recoupStatus ? STATUS_STYLE[recoupStatus].label : "N/A"
                            }
                          ]
                        }
                      ]
                    },
                    recoupEvent.px,
                    eventY
                  )
                }
              >
                <polygon
                  points={hexPoints(recoupEvent.px, eventY, EVENT_R)}
                  fill="#fff"
                  stroke={CR_COLOR}
                  strokeWidth={1.75}
                />
                <text
                  x={recoupEvent.px}
                  y={eventY + 3.5}
                  textAnchor="middle"
                  fontSize={9.5}
                  fontWeight={700}
                  fill={CR_COLOR}
                  pointerEvents="none"
                >
                  CR
                </text>
              </g>
            )}
            {drmEvents.map(({ kind, month, px, sections }) => {
              const color = kind === "issue" ? DRM_COLOR : FIX_COLOR
              // Clicking a marker limits the revenue to its tracks; clicking it again lifts the filter
              const filterKey = `${kind === "issue" ? "issue" : "fix"}:${month}`
              const selected = drmFilter === filterKey
              return (
                <g
                  key={`${kind}-${month}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => onDrmFilterChange(selected ? null : filterKey)}
                  onMouseEnter={() =>
                    showTip(
                      {
                        accent: color,
                        dark: true,
                        title: kind === "issue" ? "DRM ISSUE DETECTED" : "DRM ACTION TAKEN",
                        sub: monthLabel(month),
                        rows: [],
                        sections
                      },
                      px,
                      eventY
                    )
                  }
                >
                  <circle
                    cx={px}
                    cy={eventY}
                    r={EVENT_R}
                    fill={selected ? color : "#fff"}
                    stroke={color}
                    strokeWidth={selected ? 2.5 : 1.75}
                  />
                  <text
                    x={px}
                    y={eventY + 3.5}
                    textAnchor="middle"
                    fontSize={kind === "issue" ? 9.5 : 8.5}
                    fontWeight={700}
                    fill={selected ? "#fff" : color}
                    pointerEvents="none"
                  >
                    {kind === "issue" ? "D" : "FX"}
                  </text>
                </g>
              )
            })}

            {/* Platform lines first, All last so it stays on top */}
            {[...visibleSeries.filter((s) => s.key !== "all"), visibleSeries[0]]
              .filter((series) => !hidden.includes(series.key))
              .map((series) => {
                const isAll = series.key === "all"
                return (
                  <g key={series.key}>
                    {isAll
                      ? curveSegments(points.map((point, i): Pt => [x(i), y(point.row.total)])).map((d, k) => (
                          <path
                            key={points[k + 1].row.month}
                            d={d}
                            fill="none"
                            stroke={series.color}
                            strokeWidth={3.2}
                            strokeLinecap="round"
                            strokeDasharray={points[k + 1].status === "complete" ? undefined : "6 4"}
                          />
                        ))
                      : runsFor(series.key).map((d) => (
                          <path
                            key={d}
                            d={d}
                            fill="none"
                            stroke={series.color}
                            strokeWidth={2.2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                          />
                        ))}
                    {points.map((point, i) => {
                      const value = valueOf(point, series.key)
                      if (value === null) return null
                      const py = y(value)
                      return (
                        <g
                          key={point.row.month}
                          onMouseEnter={() =>
                            showTip(isAll ? allTip(point) : platformTip(series, point, value), x(i), py)
                          }
                        >
                          <circle cx={x(i)} cy={py} r={8} fill="transparent" />
                          {/* The All dot is a ring: r 3 plus a 2px stroke reaches a 4px outer radius, slightly larger than the filled ones. */}
                          {isAll ? (
                            <circle cx={x(i)} cy={py} r={3} fill="#fff" stroke={series.color} strokeWidth={2} />
                          ) : (
                            <circle cx={x(i)} cy={py} r={3.5} fill={series.color} />
                          )}
                        </g>
                      )
                    })}
                  </g>
                )
              })}
          </svg>

          {filtering && (
            // The shared Loading component covers the whole screen, so only its spinner is used here
            <div className="absolute inset-0 flex justify-center items-center" style={{ zIndex: 40 }}>
              <div className="loader" />
            </div>
          )}

          {tip && (
            <div
              style={{
                position: "absolute",
                zIndex: 30,
                left: flip ? undefined : tipLeft + 14,
                right: flip ? wrapWidth - tipLeft + 14 : undefined,
                top: Math.max(tip.y * scale - 20, 0),
                minWidth: 170,
                maxWidth: 260,
                padding: "10px 12px",
                background: tip.dark ? "#232637" : "#fff",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                fontSize: 12,
                lineHeight: 1.4,
                color: tip.dark ? "#fff" : TEXT_PRIMARY,
                pointerEvents: "none"
              }}
            >
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>{tip.title}</div>
              <div
                style={{
                  fontSize: 11,
                  color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED,
                  marginBottom: tip.subExtra ? 0 : 6
                }}
              >
                {tip.sub}
              </div>
              {tip.subExtra && (
                <div style={{ fontSize: 11, color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED, marginBottom: 6 }}>
                  {tip.subExtra}
                </div>
              )}
              {tip.rows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 16,
                    padding: "2px 0"
                  }}
                >
                  <span style={{ color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED }}>{row.label}</span>
                  <span style={{ fontWeight: 600, textAlign: "right" }}>{row.value}</span>
                </div>
              ))}
              {tip.sections?.map((section, index) => (
                <div
                  key={index}
                  style={
                    index > 0
                      ? {
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: `1px solid ${tip.dark ? "rgba(255,255,255,0.15)" : "#e5e7eb"}`
                        }
                      : undefined
                  }
                >
                  {section.heading && (
                    <div
                      style={{
                        margin: "8px 0 4px",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED
                      }}
                    >
                      {section.heading}
                    </div>
                  )}
                  {section.rows.map((row) => (
                    <div
                      key={row.label}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 16,
                        padding: "2px 0"
                      }}
                    >
                      <span style={{ color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED }}>{row.label}</span>
                      <span style={{ fontWeight: 600, textAlign: "right" }}>{row.value}</span>
                    </div>
                  ))}
                  {section.list?.map((line, i) => (
                    <div key={i} style={{ padding: "1px 0", overflowWrap: "anywhere" }}>
                      {line}
                    </div>
                  ))}
                  {section.note && (
                    <div
                      style={{ marginTop: 6, fontSize: 10.5, color: tip.dark ? "rgba(255,255,255,0.6)" : TEXT_MUTED }}
                    >
                      {section.note}
                    </div>
                  )}
                  {section.action && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 11px",
                        background: "#f6e3b4",
                        border: `1px solid ${FIX_COLOR}`,
                        borderRadius: 6,
                        color: "#3a2a0d"
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: "#8a6417",
                          marginBottom: 4
                        }}
                      >
                        Action Taken
                      </div>
                      <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>{section.action}</div>
                    </div>
                  )}
                </div>
              ))}
              {tip.missing && (
                <>
                  <div
                    style={{
                      margin: "8px 0 4px",
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: TEXT_MUTED
                    }}
                  >
                    Missing platforms
                  </div>
                  {tip.missing.map((name) => (
                    <div key={name} style={{ padding: "1px 0", fontWeight: 600 }}>
                      {name}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-2">
          <KSpan text={EVENTS_LEGEND} fontSize={11.5} color={TEXT_MUTED} lineHeight="16px" />
        </div>
        {noteText && <KSpan text={noteText} fontSize={11} color={TEXT_MUTED} lineHeight="16px" />}
      </>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-3 min-w-0">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <KTitleSpan text="Platform Revenue" fontSize={20} fontWeight={700} lineHeight="28px" />
        {details && (
          <div className="flex items-center flex-wrap gap-2">
            {filterButton({
              label: "DRM Affected Only",
              value: "all",
              color: DRM_COLOR,
              disabled: filterDisabled,
              emptyHint: "No DRM issues or fixes to filter by",
              hasMarkers: hasVisibleIssues
            })}
            {filterButton({
              label: "Discovery Mode Only",
              value: "dm-all",
              color: DM_COLOR,
              disabled: discoveryDisabled,
              emptyHint: "No Discovery Mode tracks to filter by",
              hasMarkers: hasVisibleDiscovery
            })}
          </div>
        )}
      </div>
      {renderBody()}
    </div>
  )
}

export default PlatformRevenueChart
